import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader, FileText, AlertTriangle, Clock, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyzerPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loadingLimits, setLoadingLimits] = useState(true);

  // Fetch limits on mount and when user changes
  useEffect(() => {
    fetchLimits();
  }, [user, token]);

  const fetchLimits = async () => {
    if (!isAuthenticated || !token) {
      // Guest user - get from localStorage
      const guestLimits = getGuestLimits();
      setLimits({
        role: 'guest',
        attempts_remaining: 10 - guestLimits.used,
        next_reset_time: new Date(guestLimits.reset_time).toISOString(),
        can_analyze: (10 - guestLimits.used) > 0
      });
      setLoadingLimits(false);
      return;
    }

    try {
      setLoadingLimits(true);
      const response = await fetch(`${API}/auth/limits`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch limits');
      }
      
      const data = await response.json();
      setLimits(data);
    } catch (err) {
      console.error('Failed to fetch limits:', err);
      setLimits(null);
    } finally {
      setLoadingLimits(false);
    }
  };

  const getGuestLimits = () => {
    const stored = localStorage.getItem('guest_analytics_limits');
    if (!stored) {
      const newLimits = {
        used: 0,
        reset_time: Date.now() + 5 * 60 * 60 * 1000  // +5 hours
      };
      localStorage.setItem('guest_analytics_limits', JSON.stringify(newLimits));
      return newLimits;
    }

    const limits = JSON.parse(stored);
    
    // Check if reset time has passed
    if (Date.now() > limits.reset_time) {
      const newLimits = {
        used: 0,
        reset_time: Date.now() + 5 * 60 * 60 * 1000
      };
      localStorage.setItem('guest_analytics_limits', JSON.stringify(newLimits));
      return newLimits;
    }
    
    return limits;
  };

  const incrementGuestAnalytics = () => {
    const limits = getGuestLimits();
    limits.used += 1;
    localStorage.setItem('guest_analytics_limits', JSON.stringify(limits));
    
    // Update UI
    fetchLimits();
  };

  const handleAnalyze = async () => {
    if (!text.trim()) {
      setError('Пожалуйста, введите текст для анализа');
      return;
    }

    // Check limits
    if (limits && !limits.can_analyze) {
      setError(`Вы исчерпали лимит попыток. Лимит обновится ${new Date(limits.next_reset_time).toLocaleString()}`);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/analyze`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text: text,
          analyze_io_type: true,
          extract_military_terms: true,
          analyze_emotion: true,
          detect_fake: true
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Ошибка анализа' }));
        throw new Error(error.detail || 'Ошибка при анализе текста');
      }

      const data = await response.json();
      setResult(data);
      
      // Increment guest analytics if not authenticated
      if (!isAuthenticated) {
        incrementGuestAnalytics();
      } else {
        // Refresh limits after successful analysis for authenticated user
        setTimeout(fetchLimits, 500);
      }
    } catch (err) {
      console.error('Analysis error:', err);
      const message = err.message || 'Ошибка при анализе текста';
      
      if (message.includes('429') || message.includes('Limit')) {
        setError('Вы исчерпали лимит попыток анализа. ' + message);
        if (!isAuthenticated) {
          setError(message + ' Пожалуйста, зарегистрируйтесь для получения большего лимита!');
        }
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getIOTypeColor = (type) => {
    const colors = {
      'DISINFORMATION': 'bg-red-100 text-red-800 border-red-300',
      'DEMORALIZATION': 'bg-orange-100 text-orange-800 border-orange-300',
      'DISCREDITATION': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'INTIMIDATION': 'bg-purple-100 text-purple-800 border-purple-300',
      'HATE_INCITEMENT': 'bg-pink-100 text-pink-800 border-pink-300',
      'PANIC_CREATION': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'PROVOCATION': 'bg-blue-100 text-blue-800 border-blue-300',
      'AUTHORITY_UNDERSCORE': 'bg-cyan-100 text-cyan-800 border-cyan-300'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getIOTypeNameRu = (type) => {
    const names = {
      'DISINFORMATION': 'Дезинформация',
      'DEMORALIZATION': 'Деморализация',
      'DISCREDITATION': 'Дискредитация',
      'INTIMIDATION': 'Запугивание',
      'HATE_INCITEMENT': 'Разжигание ненависти',
      'PANIC_CREATION': 'Создание паники',
      'PROVOCATION': 'Провокация',
      'AUTHORITY_UNDERSCORE': 'Подрыв доверия к власти'
    };
    return names[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3" data-testid="analyzer-title">
            Анализатор текста
          </h1>
          <p className="text-lg text-gray-600">
            Введите текст для анализа на наличие информационных операций
          </p>
        </div>

        {/* Limits Info Card */}
        {!loadingLimits && limits && (
          <div className={`rounded-xl shadow-sm border p-4 mb-6 flex items-center gap-4 ${
            limits.role === 'admin' 
              ? 'bg-amber-50 border-amber-200' 
              : limits.attempts_remaining > 0 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <Zap className={`w-6 h-6 flex-shrink-0 ${
              limits.role === 'admin' 
                ? 'text-amber-600' 
                : limits.attempts_remaining > 0 
                ? 'text-blue-600' 
                : 'text-red-600'
            }`} />
            <div className="flex-1">
              <p className={`font-medium ${
                limits.role === 'admin' 
                  ? 'text-amber-900' 
                  : limits.attempts_remaining > 0 
                  ? 'text-blue-900' 
                  : 'text-red-900'
              }`}>
                {limits.role === 'admin' 
                  ? 'Админ режим - неограниченные попытки' 
                  : `Осталось попыток: ${limits.attempts_remaining}`}
              </p>
              {limits.role !== 'admin' && (
                <p className={`text-sm ${
                  limits.attempts_remaining > 0 
                    ? 'text-blue-700' 
                    : 'text-red-700'
                }`}>
                  Лимит обновится: {new Date(limits.next_reset_time).toLocaleString()}
                </p>
              )}
            </div>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
              >
                Зарегистрироваться
              </Link>
            )}
          </div>
        )}

        {/* Input Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Текст для анализа
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Введите или вставьте текст здесь..."
            className="w-full h-48 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            data-testid="text-input"
            disabled={loading || (limits && !limits.can_analyze && limits.role !== 'admin')}
          />
          
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              {text.length} символов
            </span>
            <button
              onClick={handleAnalyze}
              disabled={loading || !text.trim() || (limits && !limits.can_analyze && limits.role !== 'admin')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              data-testid="analyze-button"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Анализируем...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Анализировать
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Ошибка</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="space-y-6" data-testid="results-section">
            {/* IO Types */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
                Типы информационных операций
              </h2>
              
              {result.io_types && result.io_types.length > 0 ? (
                <div className="space-y-3">
                  {result.io_types.map((io, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border ${getIOTypeColor(io.type)}`}
                      data-testid={`io-type-result-${index}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{getIOTypeNameRu(io.type)}</span>
                        <span className="text-sm font-medium">
                          Уверенность: {(io.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p className="text-sm">{io.evidence}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>Признаков информационных операций не обнаружено</p>
                </div>
              )}
            </div>

            {/* Military Terms */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Военные термины
              </h2>
              
              {result.military_terms && result.military_terms.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {result.military_terms.map((term, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                      data-testid={`military-term-${index}`}
                    >
                      {term}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Военных терминов не обнаружено</p>
              )}
            </div>

            {/* Emotional Tone */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Эмоциональная окраска
              </h2>
              <div className="text-center py-4">
                <span className="inline-block px-6 py-3 bg-purple-50 text-purple-700 rounded-lg text-lg font-medium border border-purple-200">
                  {result.emotional_tone || 'Не определена'}
                </span>
              </div>
            </div>

            {/* Fake Indicators */}
            {result.fake_indicators && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Анализ достоверности
                </h2>
                
                <div className={`p-4 rounded-lg border ${
                  result.fake_indicators.is_suspicious 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    {result.fake_indicators.is_suspicious ? (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    ) : (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    )}
                    <span className={`font-semibold ${
                      result.fake_indicators.is_suspicious 
                        ? 'text-red-900' 
                        : 'text-green-900'
                    }`}>
                      {result.fake_indicators.is_suspicious 
                        ? 'Обнаружены подозрительные признаки' 
                        : 'Признаков фейка не обнаружено'}
                    </span>
                  </div>
                  
                  {result.fake_indicators.indicators && result.fake_indicators.indicators.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium mb-2">Индикаторы:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {result.fake_indicators.indicators.map((indicator, index) => (
                          <li key={index} className="text-sm">{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="mt-3 text-sm">
                    Уверенность: {(result.fake_indicators.confidence * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyzerPage;
